import {usePath} from '../contexts/PathContext'
import {useRefInfo} from '../contexts/RefInfoContext'
import {useCurrentRepository} from '@github-ui/current-repository'
import {RepositoryErrorPage, type ErrorPageEmbeddedProps} from '@github-ui/error-pages'
import {repoDefaultBrachUrl, repoOverviewUrl} from '@github-ui/paths'
import type {PageError} from '@github-ui/react-core/app-routing-types'
import type {DataRouterErrorPayload} from '@github-ui/code-view-types'
import {LockIcon} from '@primer/octicons-react'
import {BranchName, Link} from '@primer/react'

import styles from './CodeViewError.module.css'

type CodeViewErrorProps = PageError & Partial<Pick<DataRouterErrorPayload, 'path' | 'refInfo'>>

export type MissingPathErrorPageProps = ErrorPageEmbeddedProps & {
  branchName: string
  repositoryName: string
  path: string
  repositoryOverviewUrl: string
}

export type InvalidRefErrorPageProps = ErrorPageEmbeddedProps & {
  refName: string
  defaultBranchUrl: string
}

export type NoLockfileErrorPageProps = ErrorPageEmbeddedProps & {
  repositoryOverviewUrl: string
}

export function CodeViewError({path: pathOverride, refInfo: refInfoOverride, ...error}: CodeViewErrorProps) {
  const repo = useCurrentRepository()
  const contextPath = usePath()
  const contextRefInfo = useRefInfo()
  const path = pathOverride ?? contextPath
  const refInfo = refInfoOverride ?? contextRefInfo
  const hasValidRef = Boolean(refInfo.currentOid)

  if (error.httpStatus === 404) {
    if (!hasValidRef) {
      return <InvalidRefErrorPage refName={refInfo.name} defaultBranchUrl={repoDefaultBrachUrl(repo)} isEmbedded />
    }

    if (isWorkflowLockfilePath(path)) {
      return <NoLockfileErrorPage repositoryOverviewUrl={repoOverviewUrl(repo)} isEmbedded />
    }

    return (
      <MissingPathErrorPage
        branchName={refInfo.name}
        repositoryName={repo.name}
        path={path}
        repositoryOverviewUrl={repoOverviewUrl(repo)}
        isEmbedded
      />
    )
  }

  const shouldShowFallbackAction = Boolean(path) || !hasValidRef
  const action = shouldShowFallbackAction
    ? {
        label: hasValidRef ? 'Return to the repository overview' : 'Go to default branch',
        url: hasValidRef ? repoOverviewUrl(repo) : repoDefaultBrachUrl(repo),
      }
    : undefined

  return (
    <RepositoryErrorPage
      action={action}
      className={styles.Box}
      description={<DefaultDescriptionText httpStatus={error.httpStatus} type={error.type} />}
      heading="Error loading page"
      isEmbedded
    />
  )
}

export function MissingPathErrorPage({
  branchName,
  repositoryName,
  path,
  repositoryOverviewUrl,
  isEmbedded = false,
}: MissingPathErrorPageProps) {
  return (
    <RepositoryErrorPage
      action={{label: 'Return to the repository overview', url: repositoryOverviewUrl}}
      description={
        <span data-testid="error-404-description">
          The&nbsp;
          <BranchName as="span">{branchName}</BranchName>
          &nbsp;branch of&nbsp;
          <strong>{repositoryName}</strong>
          &nbsp;does not contain the path&nbsp;
          <strong>{path}.</strong>
        </span>
      }
      isEmbedded={isEmbedded}
    />
  )
}

export function InvalidRefErrorPage({refName, defaultBranchUrl, isEmbedded = false}: InvalidRefErrorPageProps) {
  return (
    <RepositoryErrorPage
      action={{label: 'Go to default branch', url: defaultBranchUrl}}
      description={
        <span data-testid="error-404-description">
          Cannot find a valid ref in&nbsp;
          <BranchName as="span">{refName}</BranchName>
        </span>
      }
      isEmbedded={isEmbedded}
    />
  )
}

export function NoLockfileErrorPage({repositoryOverviewUrl, isEmbedded = false}: NoLockfileErrorPageProps) {
  return (
    <RepositoryErrorPage
      action={{label: 'Return to the repository overview', url: repositoryOverviewUrl}}
      description={
        <>
          No lockfile was found for the workflows in this repository. Lock your workflow dependencies by running{' '}
          <code>gh actions-lock</code> in your repository.
        </>
      }
      heading="No lockfile found"
      visual={<LockIcon size="medium" />}
      isEmbedded={isEmbedded}
    />
  )
}

function isWorkflowLockfilePath(path: string) {
  return path === '.github/workflows/actions.lock' || path === '.github/workflows-lab/actions.lock'
}

function DefaultDescriptionText({httpStatus, type}: {httpStatus?: number; type?: string}) {
  const errorNumberText = httpStatus ? ` ${httpStatus} error` : 'error'

  if (type === 'fetchError') {
    return (
      <span data-testid="fetch-error-description">
        It looks like your internet connection is down. Please check it.
      </span>
    )
  }

  return (
    <span data-testid="default-error-description">
      An unexpected {errorNumberText} occurred. Try{' '}
      <Link inline onClick={() => window.location.reload()} key="reload-page">
        reloading the page.
      </Link>
    </span>
  )
}
